import React, { useState } from 'react';

interface UserRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormErrors {
  [key: string]: string;
}

interface RegistrationResponse {
  message: string;
  user: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department: string;
  };
  debug?: {
    username: string;
    oneTimePassword: string;
    loginUrl: string;
  };
}

const UserRegistrationModal: React.FC<UserRegistrationModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RegistrationResponse | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'bruker',
    department: 'salg',
    newspaper: ''
  });

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'bruker',
        department: 'salg',
        newspaper: ''
      });
      setError(null);
      setSuccess(null);
      setFormErrors({});
      setLoading(false);
    }
  }, [isOpen]);

  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'Fornavn er påkrevd';
    } else if (formData.firstName.length < 2) {
      errors.firstName = 'Fornavn må være minst 2 tegn';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Etternavn er påkrevd';
    } else if (formData.lastName.length < 2) {
      errors.lastName = 'Etternavn må være minst 2 tegn';
    }

    if (!formData.email.trim()) {
      errors.email = 'E-post er påkrevd';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Ugyldig e-postformat';
    }

    if (formData.phone && formData.phone.length < 8) {
      errors.phone = 'Telefonnummer må være minst 8 tegn';
    }

    if (!formData.newspaper.trim()) {
      errors.newspaper = 'Avis er påkrevd';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data: RegistrationResponse = await response.json();
        setSuccess(data);
        
        // Auto-close and refresh after 5 seconds if no debug info
        if (!data.debug) {
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 3000);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'Error creating user');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSuccessClose = () => {
    onSuccess();
    onClose();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log('Copied to clipboard:', text);
    });
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames = {
      administrator: 'Administrator',
      leder: 'Leder',
      selger: 'Selger',
      bruker: 'Bruker'
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };

  const getDepartmentDisplayName = (department: string) => {
    const departmentNames = {
      salg: 'Salg',
      markedsfoering: 'Markedsføring',
      redaksjon: 'Redaksjon',
      administrasjon: 'Administrasjon',
      ledelse: 'Ledelse',
      web: 'Web'
    };
    return departmentNames[department as keyof typeof departmentNames] || department;
  };

  if (!isOpen) return null;

  return (
    <div className="user-registration-overlay" onClick={onClose}>
      <div className="user-registration-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-registration-header">
          <h2>Registrer ny bruker</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="user-registration-content">
          {error && (
            <div className="alert alert-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          {success && (
            <div className="user-registration-success">
              <div className="alert alert-success">
                <span>✅ Bruker opprettet!</span>
              </div>

              <div className="success-details">
                <h3>Bruker registrert</h3>
                <div className="user-info">
                  <p><strong>Navn:</strong> {success.user.firstName} {success.user.lastName}</p>
                  <p><strong>E-post:</strong> {success.user.email}</p>
                  <p><strong>Rolle:</strong> {getRoleDisplayName(success.user.role)}</p>
                  <p><strong>Avdeling:</strong> {getDepartmentDisplayName(success.user.department)}</p>
                </div>

                {success.debug && (
                  <div className="credentials-section">
                    <h4>Innloggingsinformasjon</h4>
                    <div className="credential-item">
                      <label>Brukernavn:</label>
                      <div className="credential-value">
                        <code>{success.debug.username}</code>
                        <button 
                          className="copy-button"
                          onClick={() => copyToClipboard(success.debug!.username)}
                          title="Kopier brukernavn"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                    <div className="credential-item">
                      <label>Midlertidig passord:</label>
                      <div className="credential-value">
                        <code className="password-code">{success.debug.oneTimePassword}</code>
                        <button 
                          className="copy-button"
                          onClick={() => copyToClipboard(success.debug!.oneTimePassword)}
                          title="Kopier passord"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                    <div className="login-info">
                      <p>
                        <strong>Viktig:</strong> Brukeren må logge inn med disse opplysningene og vil bli bedt om å 
                        endre passord ved første innlogging.
                      </p>
                      <p>
                        <strong>Innloggingslink:</strong>{' '}
                        <a href={success.debug.loginUrl} target="_blank" rel="noopener noreferrer">
                          {success.debug.loginUrl}
                        </a>
                      </p>
                    </div>
                  </div>
                )}

                <div className="success-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleSuccessClose}
                  >
                    Lukk
                  </button>
                </div>
              </div>
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="user-registration-form">
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="firstName" className="required">Fornavn</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={formErrors.firstName ? 'error' : ''}
                    disabled={loading}
                    placeholder="Skriv inn fornavn"
                  />
                  {formErrors.firstName && (
                    <span className="error-message">{formErrors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="required">Etternavn</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={formErrors.lastName ? 'error' : ''}
                    disabled={loading}
                    placeholder="Skriv inn etternavn"
                  />
                  {formErrors.lastName && (
                    <span className="error-message">{formErrors.lastName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="required">E-post</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={formErrors.email ? 'error' : ''}
                    disabled={loading}
                    placeholder="bruker@eksempel.no"
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Telefon</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={formErrors.phone ? 'error' : ''}
                    disabled={loading}
                    placeholder="12345678"
                  />
                  {formErrors.phone && (
                    <span className="error-message">{formErrors.phone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="role" className="required">Rolle</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="bruker">Bruker</option>
                    <option value="selger">Selger</option>
                    <option value="leder">Leder</option>
                    <option value="administrator">Administrator</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="department" className="required">Avdeling</label>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="salg">Salg</option>
                    <option value="markedsfoering">Markedsføring</option>
                    <option value="redaksjon">Redaksjon</option>
                    <option value="administrasjon">Administrasjon</option>
                    <option value="ledelse">Ledelse</option>
                    <option value="web">Web</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="newspaper" className="required">Avis</label>
                  <input
                    type="text"
                    id="newspaper"
                    name="newspaper"
                    value={formData.newspaper}
                    onChange={handleInputChange}
                    className={formErrors.newspaper ? 'error' : ''}
                    disabled={loading}
                    placeholder="Navn på avis/publikasjon"
                  />
                  {formErrors.newspaper && (
                    <span className="error-message">{formErrors.newspaper}</span>
                  )}
                </div>
              </div>

              <div className="form-info">
                <p className="info-text">
                  <strong>Merk:</strong> Brukernavn genereres automatisk basert på for- og etternavn. 
                  Et midlertidig passord vil bli opprettet som brukeren må endre ved første innlogging.
                </p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Oppretter bruker...' : 'Opprett bruker'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRegistrationModal;